import { ErrorModal, MesfModal } from "@dexteel/mesf-core";
import {
	capitalize,
	Checkbox,
	DialogProps,
	FormControl,
	FormControlLabel,
	FormHelperText,
	Grid2,
	TextField,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";

import { FooterModalButtons } from "@/controls/buttons/FooterModalButtons";
import { CenteredLazyLoading } from "@/controls/LazyLoading";

import { HelmetDexteel } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import { enqueueSnackbar } from "notistack";
import { LocationTreeSelector } from "../../../../../shared/selectors";
import {
	CATALOG_LOCATION_INITIAL_VALUES,
	CatalogLocation,
} from "../../../models/CatalogLocation";
import {
	deleteLocation,
	getLocations,
	upsertLocation,
} from "../../../repositories/CatalogLocationRepository";
import { useCatalogLocationContext } from "../context/CatalogLocationContext";

type Props = {
	LocationId: number | null | undefined;
	show: boolean;
	modal: "NEW" | "EDIT" | "DELETE" | "VIEW" | "";
	onHide: (shouldUpdate: boolean) => void;
};

export const UpsertDeleteViewLocation = ({
	LocationId,
	show,
	modal,
	onHide,
}: Props) => {
	const maxWidth: DialogProps["maxWidth"] = "sm";
	const [error, setError] = useState<string>("");
	const [isSubmitLoading, setIsSubmitLoading] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(false);

	const successMessage = `The location was ${
		modal === "NEW" ? "created" : modal === "EDIT" ? "updated" : "deleted"
	} successfully`;
	const optionsTypeCode = [{ Name: "Mill" }, { Name: "Warehouse" }];

	const {
		state: { allLocations, locationNodeSelectedInTree },
		actions: {
			setLocationParentIdToCreateLocation,
			setLocationNameToCreateLocation,
		},
	} = useCatalogLocationContext();

	const { control, handleSubmit, reset, setValue, getValues } =
		useForm<CatalogLocation>({
			defaultValues: CATALOG_LOCATION_INITIAL_VALUES,
		});

	const handleCancel = () => {
		onHide(false);
	};

	const onSubmit: SubmitHandler<CatalogLocation> = async (data: any) => {
		setIsSubmitLoading(true);
		if (modal === "DELETE") {
			const locationResp = await deleteLocation(data.LocationId);
			if (locationResp.ok) {
				enqueueSnackbar(successMessage, { variant: "success" });
				onHide(true);
			} else {
				setError(locationResp.message);
			}
		} else {
			const locationResp = await upsertLocation(data);
			if (locationResp.ok) {
				enqueueSnackbar(successMessage, { variant: "success" });
				onHide(true);
			} else {
				setError(locationResp.message);
			}
		}
		setIsSubmitLoading(false);
	};

	useEffect(() => {
		if (show) {
			reset(CATALOG_LOCATION_INITIAL_VALUES);
			if (modal !== "NEW") {
				(async () => {
					setIsLoading(true);
					const response = await getLocations(LocationId);
					if (response.ok) {
						const location = get(
							response,
							"data.tables[0].rows[0]",
							null,
						) as CatalogLocation;
						if (location) {
							reset(location);
						}
					} else {
						setError(response.message);
						return;
					}
					setIsLoading(false);
				})();
			} else {
				const parentRoot: CatalogLocation = allLocations.find(
					(node: CatalogLocation) => {
						if (locationNodeSelectedInTree)
							return node.LocationId === locationNodeSelectedInTree.id;
						else return node.LocationId === null;
					},
				);
				if (parentRoot) {
					setLocationParentIdToCreateLocation(parentRoot.LocationId);
					setValue("ParentLocationId", parentRoot.LocationId);
				}
				setLocationNameToCreateLocation("");
			}
		}
	}, [LocationId, show]);

	return (
		<>
			{show && (
				<HelmetDexteel
					title={`${capitalize(modal.toLowerCase())} Location - Locations Tree`}
				/>
			)}
			<MesfModal
				title={`${modal} LOCATION`}
				maxWidth={maxWidth}
				open={show}
				handleClose={() => handleCancel()}
			>
				<form onSubmit={handleSubmit(onSubmit)}>
					<MesfModal.Content dividers style={{ padding: "10px 30px" }}>
						{isLoading && <CenteredLazyLoading />}
						{!isLoading && (
							<>
								<Grid2 container spacing={1} alignItems={"center"}>
									<Grid2 size={{ xs: 10, sm: 12, md: 3 }}>
										<Controller
											name="LocationTypeCode"
											control={control}
											render={({ field }) => (
												<>
													<TextField
														label="Type"
														variant="outlined"
														value={
															optionsTypeCode.find(
																(code) => code.Name.at(0) === field.value,
															)?.Name
														}
														disabled
														margin="dense"
														size="small"
														fullWidth
													/>
												</>
											)}
										/>
									</Grid2>
									<Grid2 size={{ xs: 10, sm: 12, md: 6 }}>
										<Controller
											name="LocationName"
											control={control}
											rules={{ required: "Name is required" }}
											render={({
												field: { onChange, value },
												fieldState: { error },
											}) => (
												<TextField
													required
													label="Location Name"
													variant="outlined"
													fullWidth
													margin="dense"
													size="small"
													autoComplete="off"
													onChange={onChange}
													value={value}
													disabled={modal === "DELETE" || modal === "VIEW"}
													error={!!error}
													helperText={error?.message}
												/>
											)}
										/>
									</Grid2>
									<Grid2 size={{ xs: 10, sm: 12, md: 3 }}>
										<Controller
											name="LocationKey"
											control={control}
											render={({ field: { value, onChange } }) => (
												<>
													<TextField
														label="Key"
														variant="outlined"
														value={value}
														onChange={onChange}
														margin="dense"
														size="small"
														fullWidth
														inputProps={{ maxLength: 2 }}
													/>
												</>
											)}
										/>
									</Grid2>
									{(getValues("ParentLocationId") || modal === "NEW") && (
										<Grid2 size={{ xs: 10, sm: 12, md: 12 }}>
											<Controller
												name="ParentLocationId"
												control={control}
												rules={{ required: "Parent is required" }}
												render={({ field, fieldState: { error } }) => (
													<FormControl error={!!error} fullWidth>
														<LocationTreeSelector
															label="Parent"
															value={field.value}
															onChange={field.onChange}
															disabled={modal === "DELETE" || modal === "VIEW"}
														/>
														{error && (
															<FormHelperText>{error.message}</FormHelperText>
														)}
													</FormControl>
												)}
											/>
										</Grid2>
									)}
									<Grid2 size={{ xs: 12, sm: 12, md: 8 }}>
										<Controller
											name="RFIDTag"
											control={control}
											render={({ field }) => (
												<TextField
													value={field.value}
													onChange={field.onChange}
													label="Tag"
													variant="outlined"
													size="small"
													fullWidth
													disabled={modal === "DELETE" || modal === "VIEW"}
												/>
											)}
										/>
									</Grid2>
									<Grid2 size={{ xs: 12, sm: 12, md: 4 }}>
										<Controller
											name="IsExclusive"
											control={control}
											render={({ field }) => (
												<FormControlLabel
													control={
														<Checkbox
															checked={field.value}
															onChange={field.onChange}
															name="IsCircular"
															color="primary"
															disabled={modal === "DELETE" || modal === "VIEW"}
														/>
													}
													label="Is Exclusive"
												/>
											)}
										/>
									</Grid2>
								</Grid2>
							</>
						)}
					</MesfModal.Content>
					{modal !== "VIEW" && (
						<MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
							<FooterModalButtons
								isSubmitLoading={isSubmitLoading}
								onHide={handleCancel}
								buttonLabel={modal === "DELETE" ? "Delete" : "Save"}
								buttonColor={modal === "DELETE" ? "secondary" : "primary"}
							/>
						</MesfModal.Actions>
					)}
				</form>
			</MesfModal>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
